import React, {useCallback, useEffect, useRef, useState} from 'react';
import './DocumentPreview.css';
import NModal from "../../../../simple/Modals/NModal/NModal";
import Text4 from "../../../../simple/TextComponents/Text4/Text4";
import BtnNewItem from "../../../../simple/Buttons/BtnNewItem/BtnNewItem";
import api from "../../../../../services/apiInstance/api";
import apiErrorHandler from "../../../../../services/apiInstance/apiErrorHandler";
import createTempLinkToDownloadFile from "../../../../../utils/createTempLinkToDownloadFile";
import getDocumentType from "../../../../../utils/getDocumentType";
import FileViewer from 'react-file-viewer';
import OnlyOfficeViewer from './OnlyOfficeViewer';
import RenderOnReady from "../../../../service/RenderOnReady/RenderOnReady";
import text from "../../../../../services/localization/text";
import {debug, debugWarn, debugError} from "../../../../../utils/debug";

// DRY: единый набор поддерживаемых типов
const ONLYOFFICE_SUPPORTED_TYPES = ['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt', 'rtf'];
const SUPPORTED_TYPES = ['txt', 'pdf', ...ONLYOFFICE_SUPPORTED_TYPES];

const isExternalUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  try {
    const u = new URL(url, window.location.origin);
    return !!u.protocol && (u.origin !== window.location.origin);
  } catch (e) {
    return false;
  }
};

const DocumentPreview = (props) => {
  const {document, isOpen, onClose, downloadLink, itemType, organizationId} = props;

  const [previewUrl, setPreviewUrl] = useState(null);
  const [onlyOfficeUrl, setOnlyOfficeUrl] = useState(null);
  const [onlyOfficeUnavailable, setOnlyOfficeUnavailable] = useState(false);
  const [isReadyToRender, setIsReadyToRender] = useState(true);
  const [errorObject, setErrorObject] = useState(null);
  const [fileType, setFileType] = useState('');

  // Guard от повторных запросов (StrictMode, быстрые повторные открытия)
  const previewInFlightRef = useRef(false);
  const lastPreviewKeyRef = useRef(null);

  const buildOrganizationsPreviewEndpoint = (url) => {
    debug('[DocumentPreview]', '🔧 buildOrganizationsPreviewEndpoint вызван с:', url);
    if (!url) {
      debugError('[DocumentPreview]', '❌ buildOrganizationsPreviewEndpoint: url пустой');
      return null;
    }

    // Проверяем наличие /downloadDocument/ в URL
    // Формат: /{itemType}/{itemId}/downloadDocument/{fileId}/{fileName}?o_id={organizationId}
    const hasDownloadDocument = url.includes('/downloadDocument/');
    debug('[DocumentPreview]', '🔧 buildOrganizationsPreviewEndpoint проверка:', {
      hasDownloadDocument,
      url,
      urlType: typeof url
    });

    if (hasDownloadDocument) {
      // Заменяем /downloadDocument/ на /getDocumentPreview/
      const result = url.replace('/downloadDocument/', '/getDocumentPreview/');
      debug('[DocumentPreview]', '✅ buildOrganizationsPreviewEndpoint результат:', {
        original: url,
        transformed: result,
        replaced: url !== result
      });
      return result;
    }

    debug('[DocumentPreview]', '❌ buildOrganizationsPreviewEndpoint: URL не содержит /downloadDocument/');
    debug('[DocumentPreview]', '🔍 Попытка найти альтернативные паттерны в URL:', {
      url,
      includesDownload: url.includes('download'),
      includesDocument: url.includes('document'),
      urlParts: url.split('/')
    });
    return null;
  };

  const loadDocumentPreview = useCallback(() => {
    setIsReadyToRender(false);
    setErrorObject(null);
    setOnlyOfficeUnavailable(false);
    setOnlyOfficeUrl(null); // Сбрасываем при новой загрузке

    if (document.file) {
      const url = window.URL.createObjectURL(document.file);
      const docType = getDocumentType(document.file.name).toLowerCase();

      setPreviewUrl(url);
      setFileType(docType);
      setIsReadyToRender(true);
      return;
    }

    if (!downloadLink) {
      setErrorObject({message: 'Неподдерживаемый тип документа для предпросмотра'});
      setIsReadyToRender(true);
      return;
    }

    const docType = getDocumentType(document.name).toLowerCase();
    setFileType(docType);

    const previewEndpoint = buildOrganizationsPreviewEndpoint(downloadLink);

    // Проверяем, поддерживается ли тип ONLYOFFICE
    const isOnlyOfficeType = ONLYOFFICE_SUPPORTED_TYPES.includes(docType);

    // Если тип поддерживается ONLYOFFICE, но previewEndpoint null - показываем ошибку
    if (isOnlyOfficeType && !previewEndpoint) {
      const errorMessage = `Не удалось построить URL для предпросмотра документа. URL загрузки не соответствует ожидаемому формату: ${downloadLink || 'отсутствует'}`;
      debugError('[DocumentPreview]', '❌', errorMessage);
      setErrorObject({
        message: errorMessage,
        details: `Тип документа: ${docType}, downloadLink: ${downloadLink}`
      });
      setIsReadyToRender(true);
      return;
    }

    if (previewEndpoint && isOnlyOfficeType) {
      const key = `preview:${previewEndpoint}`;
      if (previewInFlightRef.current || lastPreviewKeyRef.current === key) {
        debug('[DocumentPreview]', '⏭️ Пропуск запроса (уже выполняется или кэширован):', key);
        setIsReadyToRender(true);
        return;
      }
      previewInFlightRef.current = true;
      debug('[DocumentPreview]', '📤 Запрос previewUrl:', previewEndpoint);
      api.previewUrl(previewEndpoint)
        .then(({data}) => {
          debug('[DocumentPreview]', '📥 Ответ previewUrl:', data);
          const resolvedUrl = data && data.url ? data.url : null;
          debug('[DocumentPreview]', '🔗 Извлеченный URL:', resolvedUrl);
          if (resolvedUrl) {
            setOnlyOfficeUrl(resolvedUrl);
            lastPreviewKeyRef.current = key;
            setIsReadyToRender(true);
            debug('[DocumentPreview]', '✅ onlyOfficeUrl установлен:', resolvedUrl);
          } else {
            debugWarn('[DocumentPreview]', '⚠️ URL не получен из ответа, только просмотр недоступен');
            setOnlyOfficeUnavailable(true);
            setIsReadyToRender(true);
          }
        })
        .catch((error) => {
          debugError('[DocumentPreview]', '❌ Ошибка получения previewUrl:', error);
          setOnlyOfficeUnavailable(true);
          setIsReadyToRender(true);
        })
        .finally(() => {
          previewInFlightRef.current = false;
        });
      return;
    }
    const downloadKey = `download:${downloadLink}`;
    if (previewInFlightRef.current || lastPreviewKeyRef.current === downloadKey) {
      setIsReadyToRender(true);
      return;
    }
    previewInFlightRef.current = true;
    api.download(downloadLink)
      .then(({data}) => {
        const url = window.URL.createObjectURL(new Blob([data]));
        setPreviewUrl(url);
        lastPreviewKeyRef.current = downloadKey;
        setIsReadyToRender(true);
      })
      .catch(error => {
        setIsReadyToRender(true);
        setErrorObject(apiErrorHandler(error));
      })
      .finally(() => {
        previewInFlightRef.current = false;
      });
  }, [document.name, downloadLink, document.file]);

  useEffect(() => {
    if (isOpen) {
      loadDocumentPreview();
    }
  }, [isOpen, loadDocumentPreview]);

  // Чистим ошибку при закрытии, чтобы при следующем открытии не мигала старая ошибка
  useEffect(() => {
    if (!isOpen) {
      setErrorObject(null);
      // Сбрасываем кэш при закрытии, чтобы buildOrganizationsPreviewEndpoint пересчитывался при повторном открытии
      previewInFlightRef.current = false;
      lastPreviewKeyRef.current = null;
      // Очищаем состояние для полного сброса
      setPreviewUrl(null);
      setOnlyOfficeUrl(null);
      setOnlyOfficeUnavailable(false);
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (previewUrl && typeof previewUrl === 'string' && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleError = useCallback((error) => {
    setErrorObject({message: `Ошибка отображения документа: ${error.message}`});
  }, []);

  const handleOnlyOfficeError = useCallback((error) => {
    setErrorObject({message: `Ошибка ONLYOFFICE: ${error.message || 'Не удалось загрузить документ'}`});
  }, []);

  const handleOnlyOfficeReady = useCallback(() => {
    debug('[DocumentPreview]', 'ONLYOFFICE документ загружен');
  }, []);

  const renderPreviewContent = () => {
    if (errorObject) {
      return (
        <div className="DocumentPreview__error">
          <Text4>Ошибка загрузки документа: {errorObject.message}</Text4>
        </div>
      );
    }

    if (!previewUrl && !onlyOfficeUrl && !onlyOfficeUnavailable) {
      return (
        <div className="DocumentPreview__loading">
          <Text4>Загрузка документа...</Text4>
        </div>
      );
    }

    if (ONLYOFFICE_SUPPORTED_TYPES.includes(fileType) && onlyOfficeUnavailable) {
      return (
        <div className="DocumentPreview__unsupported">
          <Text4>{text.documentPreviewTemporarilyUnavailable}</Text4>
        </div>
      );
    }

    if (!SUPPORTED_TYPES.includes(fileType)) {
      return (
        <div className="DocumentPreview__unsupported">
          <Text4>Предпросмотр для файлов типа .{fileType} не поддерживается</Text4>
          {previewUrl && (
            <BtnNewItem onClick={() => createTempLinkToDownloadFile(previewUrl, document.name)}>
              Скачать документ
            </BtnNewItem>
          )}
        </div>
      );
    }

    // Текстовые файлы
    if (fileType === 'txt' && previewUrl) {
      return (
        <div className="DocumentPreview__text-content">
          <iframe
            src={previewUrl}
            style={{
              width: '100%',
              height: '500px',
              border: 'none',
              backgroundColor: '#f5f5f5'
            }}
            title={`Предпросмотр ${document.name}`}
          />
        </div>
      );
    }

    // Office документы через ONLYOFFICE (теперь URL берем из previewUrl)
    if (ONLYOFFICE_SUPPORTED_TYPES.includes(fileType)) {
      return (
        <div className="DocumentPreview__file-viewer DocumentPreview__a4-container">
          <OnlyOfficeViewer
            documentUrl={onlyOfficeUrl}
            documentName={document.name}
            fileId={document.fileId}
            organizationId={organizationId}
            entityType={itemType}
            onError={handleOnlyOfficeError}
            onReady={handleOnlyOfficeReady}
          />
        </div>
      );
    }

    // PDF: внешние — через iframe (обход CORS), внутренние/blob — через FileViewer
    if (fileType === 'pdf' && previewUrl) {
      if (isExternalUrl(previewUrl)) {
        return (
          <div className="DocumentPreview__file-viewer DocumentPreview__a4-container">
            <iframe
              src={previewUrl}
              style={{
                width: '100%',
                height: '500px',
                border: 'none'
              }}
              title={`Предпросмотр ${document.name}`}
            />
            <div style={{ marginTop: 8 }}>
              <a href={previewUrl} target="_blank" rel="noreferrer">Открыть в новой вкладке</a>
            </div>
          </div>
        );
      }
      return (
        <div className="DocumentPreview__file-viewer DocumentPreview__a4-container">
          <FileViewer
            loaders={['pdf', 'docx', 'doc', 'xlsx', 'xls']}
            fileType={fileType}
            filePath={previewUrl}
            onError={handleError}
            errorComponent={
              <div className="DocumentPreview__error">
                <Text4>Ошибка отображения документа</Text4>
              </div>
            }
          />
        </div>
      );
    }

    // Fallback на FileViewer для остальных случаев, где есть blob-url
    if (previewUrl) {
      return (
        <div className="DocumentPreview__file-viewer DocumentPreview__a4-container">
          <FileViewer
            loaders={['pdf', 'docx', 'doc', 'xlsx', 'xls']}
            fileType={fileType}
            filePath={previewUrl}
            onError={handleError}
            errorComponent={
              <div className="DocumentPreview__error">
                <Text4>Ошибка отображения документа</Text4>
              </div>
            }
          />
        </div>
      );
    }

    return (
      <div className="DocumentPreview__unsupported">
        <Text4>{text.documentPreviewTemporarilyUnavailable}</Text4>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <NModal
      modalTitle={`Предпросмотр: ${document.name}`}
      onModalCancelBtnClick={onClose}
      modalCancelBtnText="Закрыть"
      addClasses="DocumentPreview__modal"
      isContentOverflowScroll={false}
    >
      <RenderOnReady isReadyToRender={isReadyToRender} errorObject={errorObject}/>
      <div className="DocumentPreview__content">
        {renderPreviewContent()}
      </div>
    </NModal>
  );
};

export default DocumentPreview;
